import { timestampDate } from '@bufbuild/protobuf/wkt';
import { Client } from '@connectrpc/connect';

import CancelToken from './cancelToken';
import { IntegrationsService } from './espb/integrations_service_pb';
import { requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto } from './sync';
import {
  EntraTenantDto,
  EntraUserId,
  GoogleWorkspaceIntegrationDto,
  OrganizationId,
  RoleId,
  SyncUpdate,
} from './types';

export default class IntegrationsServiceClient {
  constructor(private readonly espbClient: Client<typeof IntegrationsService>) {}

  async entraFinishAuthorization(resultQueryString: string): Promise<{
    syncUpdate: SyncUpdate;
    organizationId: OrganizationId;
  }> {
    const resp = await this.espbClient.entraFinishAuthorization({ resultQueryString });
    return {
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
      organizationId: requireFieldNotEmpty(resp.organizationId),
    };
  }

  async entraGetAuthorizeUri(organizationId: OrganizationId, redirectUri: string): Promise<string> {
    const resp = await this.espbClient.entraGetAuthorizeUri({ organizationId, redirectUri });
    return requireFieldNotEmpty(resp.uri);
  }

  async entraGetTenant(
    organizationId: OrganizationId,
    forceRefresh?: boolean,
  ): Promise<EntraTenantDto> {
    const resp = await this.espbClient.entraGetTenant({ organizationId, forceRefresh });

    return {
      synchronizedAt: resp.synchronizedAt ? timestampDate(resp.synchronizedAt) : undefined,
      isUnauthorized: resp.isUnauthorized,
      users: resp.users.map((u) => ({
        id: u.entraUserId,
        email: u.email,
        groups: u.memberOf,
        matchesSyncFilter: u.matchesSyncFilter,
        accountEnabled: u.accountEnabled,
      })),
      groups: resp.groups.map((g) => ({
        id: g.entraGroupId,
        name: g.name,
      })),
    };
  }

  async entraClearAuthorization(organizationId: OrganizationId): Promise<SyncUpdate> {
    const resp = await this.espbClient.entraClearAuthorization({ organizationId });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async entraGetSyncOperations(organizationId: OrganizationId): Promise<{
    createProfiles: {
      entraUserId: EntraUserId;
      email: string;
      roleId: RoleId;
    }[];
  }> {
    const resp = await this.espbClient.entraGetSyncOperations({ organizationId });

    return {
      createProfiles: resp.createProfiles,
    };
  }

  async entraSetSettings(
    organizationId: OrganizationId,
    entraSettings: string,
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.entraSetSettings({ organizationId, entraSettings });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async csvSetIntegrationData(
    organizationId: OrganizationId,
    dataJson: string | undefined,
    cancelToken?: CancelToken,
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.csvSetIntegrationData(
      { organizationId, dataJson },
      { signal: cancelToken?.signal },
    );
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async csvGetIntegrationData(organizationId: OrganizationId): Promise<string> {
    const resp = await this.espbClient.csvGetIntegrationData({ organizationId });
    return requireFieldNotEmpty(resp.dataJson);
  }

  async googleWorkspaceGetAuthorizationUri(
    organizationId: OrganizationId,
    redirectUri: string,
  ): Promise<string> {
    const resp = await this.espbClient.gWorkspaceGetAuthorizeUri({ organizationId, redirectUri });
    return requireFieldNotEmpty(resp.uri);
  }

  async googleWorkspaceFinishAuthorization(resultQueryString: string): Promise<{
    syncUpdate: SyncUpdate;
    organizationId: OrganizationId;
    error?: string;
    errorDescription?: string;
  }> {
    const resp = await this.espbClient.gWorkspaceFinishAuthorization({ resultQueryString });
    return {
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
      organizationId: requireFieldNotEmpty(resp.organizationId),
    };
  }

  async googleWorkspaceClearAuthorization(organizationId: OrganizationId): Promise<SyncUpdate> {
    const resp = await this.espbClient.gWorkspaceClearAuthorization({ organizationId });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async googleWorkspaceList(
    organizationId: OrganizationId,
    forceRefresh = false,
    cancelToken?: CancelToken,
  ): Promise<GoogleWorkspaceIntegrationDto> {
    const resp = await this.espbClient.gWorkspaceList(
      { organizationId, forceRefresh },
      { signal: cancelToken?.signal },
    );

    return {
      updatedAt: timestampDate(requireFieldNotEmpty(resp.updatedAt)),
      users: resp.users.map((u) => ({
        id: u.id,
        email: u.email,
      })),
    };
  }
}
